import { useState, useEffect, useCallback } from "react";
import { useSearch } from "wouter";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from "@stripe/react-stripe-js";
import { Loader2, ShieldCheck, Lock, Tag } from "lucide-react";
import { resolveReferralCode, setReferralCookie } from "@/lib/referralCookie";

const PLAN_LABELS: Record<string, string> = {
  lite: "Lite",
  standard: "Standard",
  pro: "Pro",
};

function formatCents(cents: number): string {
  return `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 0 })}`;
}

interface InitData {
  clientSecret: string;
  publishableKey: string;
  planName: string;
  billingPeriod: string;
  monthlyAmountCents: number;
  yearlyAmountCents: number;
  setupAmountCents: number;
  isTest: boolean;
}

export default function Scale360Checkout() {
  const search = useSearch();
  const params = new URLSearchParams(search);
  const plan = params.get("plan") ?? "";
  const period = params.get("period") ?? "monthly";
  const urlRef = params.get("ref") ?? "";
  // URL ref wins when present; otherwise fall back to the 90-day wbz_ref
  // cookie (set by this page or by the /join plan page) so a rep still gets
  // credit when a prospect lands here directly without the ref in the URL.
  const ref = resolveReferralCode(urlRef);
  const vertical = params.get("vertical") ?? "";
  const source = params.get("source") ?? "";
  const test = params.get("test") ?? "";
  // Verified Scale360 report ids from an Ask Scale360 link; the server re-validates their shape.
  const s360Analysis = params.get("s360_analysis") ?? "";
  const s360Audit = params.get("s360_audit") ?? "";

  const [initData, setInitData] = useState<InitData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [stripePromise, setStripePromise] = useState<ReturnType<typeof loadStripe> | null>(null);

  useEffect(() => {
    if (urlRef) setReferralCookie(urlRef);
  }, [urlRef]);

  useEffect(() => {
    if (!plan) {
      setError("No plan selected. Please go back and choose a plan.");
      return;
    }

    const query = new URLSearchParams({ plan, period });
    if (ref) query.set("ref", ref);
    if (vertical) query.set("vertical", vertical);
    if (source) query.set("source", source);
    if (test) query.set("test", test);
    if (s360Analysis) query.set("s360_analysis", s360Analysis);
    if (s360Audit) query.set("s360_audit", s360Audit);

    fetch(`/api/billing/scale360/checkout/init?${query.toString()}`)
      .then(r => r.json())
      .then((data: InitData & { error?: string }) => {
        if (data.error) { setError(data.error); return; }
        setInitData(data);
        setStripePromise(loadStripe(data.publishableKey));
      })
      .catch(() => setError("Failed to start checkout. Please try again."));
  }, [plan, period, ref, vertical, source, test, s360Analysis, s360Audit]);

  const fetchClientSecret = useCallback(
    () => Promise.resolve(initData?.clientSecret ?? ""),
    [initData?.clientSecret]
  );

  const planLabel = initData?.planName ?? PLAN_LABELS[plan] ?? plan;
  const isYearly = period === "yearly";
  const priceDisplay = initData
    ? isYearly
      ? `${formatCents(initData.yearlyAmountCents)}/year`
      : `${formatCents(initData.monthlyAmountCents)}/month`
    : null;
  // The one-time setup fee applies to every plan, monthly and annual.
  const setupDisplay =
    initData && initData.setupAmountCents > 0
      ? `+ ${formatCents(initData.setupAmountCents)} one-time setup fee`
      : null;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header — dark bg so white Scale360 logo is visible */}
      <header style={{ background: "#111827" }}>
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="https://scale360.wibiz.ai/wp-content/uploads/2026/03/scale-logo-1.png"
              alt="Scale360"
              className="h-8 w-auto object-contain"
            />
            {initData?.isTest && (
              <span className="text-xs bg-blue-500/20 text-blue-300 font-medium px-2 py-0.5 rounded-full border border-blue-500/30">
                Test Mode
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 text-sm text-gray-400">
            <Lock className="w-3.5 h-3.5" />
            Secured by Stripe
          </div>
        </div>
      </header>

      <div className="flex-1 max-w-5xl mx-auto w-full px-4 py-8 grid grid-cols-1 lg:grid-cols-5 gap-8">
        {/* Left: order summary */}
        <aside className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1">
              Your Plan
            </p>
            <h1 className="text-2xl font-bold text-gray-900 mb-1">
              {planLabel}
            </h1>
            <p className="text-sm text-gray-500 capitalize mb-4">
              {isYearly ? "Annual" : "Monthly"} subscription
            </p>

            {ref && (
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-50 border border-green-200 text-xs font-semibold text-green-700 mb-4">
                <Tag className="w-3 h-3" />
                Referral code: <span className="font-mono">{ref}</span>
              </div>
            )}

            {priceDisplay && (
              <div className="border-t border-gray-100 pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subscription</span>
                  <span className="font-semibold text-gray-900">{priceDisplay}</span>
                </div>
                {setupDisplay && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Setup fee</span>
                    <span className="font-semibold text-gray-900">
                      {formatCents(initData!.setupAmountCents)}
                    </span>
                  </div>
                )}
                {setupDisplay && (
                  <div className="flex justify-between text-sm border-t border-gray-100 pt-2">
                    <span className="font-semibold text-gray-900">Due today</span>
                    <span className="font-semibold text-gray-900">
                      {formatCents(
                        (isYearly ? initData!.yearlyAmountCents : initData!.monthlyAmountCents) +
                          initData!.setupAmountCents,
                      )}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="space-y-3">
            <div className="flex items-start gap-2 text-sm text-gray-600">
              <ShieldCheck className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
              <span>Your payment is encrypted and secure</span>
            </div>
            <div className="flex items-start gap-2 text-sm text-gray-600">
              <ShieldCheck className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
              <span>Cancel anytime — no long-term contract</span>
            </div>
            <div className="flex items-start gap-2 text-sm text-gray-600">
              <ShieldCheck className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
              <span>Onboarding support included</span>
            </div>
          </div>
        </aside>

        {/* Right: Stripe embedded checkout */}
        <main className="lg:col-span-3">
          {error ? (
            <div className="bg-white rounded-xl border border-red-200 shadow-sm p-8 text-center">
              <p className="text-red-600 font-medium mb-2">Unable to start checkout</p>
              <p className="text-sm text-gray-500">{error}</p>
              <a
                href="https://scale360.wibiz.ai/"
                className="mt-4 inline-block text-sm font-medium text-orange-600 hover:underline"
                style={{ color: "#E8620A" }}
              >
                ← Back to Scale360
              </a>
            </div>
          ) : !initData || !stripePromise ? (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-16 flex flex-col items-center justify-center gap-3">
              <Loader2 className="w-7 h-7 animate-spin text-gray-400" />
              <p className="text-sm text-gray-500">Preparing your checkout…</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <EmbeddedCheckoutProvider
                stripe={stripePromise}
                options={{ fetchClientSecret }}
              >
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            </div>
          )}
        </main>
      </div>

      <footer className="py-4 text-center text-xs text-gray-400">
        © {new Date().getFullYear()} Scale360 · Powered by{" "}
        <a
          href="https://stripe.com"
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:text-gray-600"
        >
          Stripe
        </a>
      </footer>
    </div>
  );
}
